import React, { useState, useMemo, useEffect } from "react";
import AdminLayout from "../admin/AdminDashboardLayout";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWithinInterval } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Mail,
  Calendar,
  TrendingUp,
  Download,
  Filter,
  DollarSign,
  BarChart3,
  Info,
  ChevronDown,
  X,
  Loader2,
} from "lucide-react";
import Button from "../../utils/Button";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useAuth } from "../../contexts/AuthContext";
import { ApiRequestOptions } from "../../utils/api";
import { Refresh } from "@mui/icons-material";

// Types and Interfaces
interface DailyUsage {
  date: string;
  smsCount: number;
  emailCount: number;
  smsCost: number;
  emailCost: number;
  totalCost: number;
  trend: "UP" | "DOWN" | "NEUTRAL";
}

interface MessagingUsageResponse {
  dailyTotal: number;
  date: string;
  emailCost: number;
  emailCount: number;
  smsCost: number;
  smsCount: number;
  trend: "UP" | "DOWN" | "NEUTRAL";
}

interface PartnerProfile {
  name: string;
  orgType: string;
  primaryEmail: string;
  contact: string;
  website: string;
  partnerIdentifier: string;
  messagingEnabled?: boolean;
  voteByMailReminderEnabled?: boolean;
  enabledModules?: string[];
  id: number;
}

interface MessagingUsageData {
  monthToDate: {
    smsCount: number;
    emailCount: number;
    smsCost: number;
    emailCost: number;
    totalCost: number;
  };
  dailyBreakdown: DailyUsage[];
}

const transformApiData = (apiData: { data: MessagingUsageResponse[] }): MessagingUsageData => {
  const dailyBreakdown = apiData?.data?.map(day => {
    return {
      date: day?.date,
      smsCount: day?.smsCount,
      emailCount: day?.emailCount,
      smsCost: day?.smsCost,
      emailCost: day?.emailCost,
      totalCost: day?.dailyTotal,
      trend: day?.trend,
    };
  }) || [];

  const monthToDate = dailyBreakdown.reduce(
    (acc, day) => ({
      smsCount: acc.smsCount + day.smsCount,
      emailCount: acc.emailCount + day.emailCount,
      smsCost: acc.smsCost + day.smsCost,
      emailCost: acc.emailCost + day.emailCost,
      totalCost: acc.totalCost + day.totalCost,
    }),
    { smsCount: 0, emailCount: 0, smsCost: 0, emailCost: 0, totalCost: 0 }
  );

  return { monthToDate, dailyBreakdown };
};

// Modern Card Component
const ModernCard = ({ children, className = "", ...props }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}
    {...props}
  >
    {children}
  </motion.div>
);

// Metric Card Component
const MetricCard = ({
  icon,
  title,
  value,
  change,
  color = "blue"
}: {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  change?: string;
  color?: string;
}) => {
  const colorClasses = {
    blue: "from-blue-500 to-cyan-600",
    purple: "from-purple-500 to-pink-600",
    green: "from-green-500 to-emerald-600",
    orange: "from-orange-500 to-red-600",
  };

  return (
    <ModernCard className="group hover:scale-[1.02]">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
      <div className="relative p-4">
        <div className="flex justify-between items-start mb-6">
          <div className={`p-3 bg-gradient-to-br ${colorClasses[color as keyof typeof colorClasses]} rounded-xl text-white shadow-lg`}>
            {icon}
          </div>
          {change && (
            <div className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200/50">
              {change}
            </div>
          )}
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
          {value}
        </h3>
        <p className="text-sm font-medium text-gray-600">{title}</p>
      </div>
    </ModernCard>
  );
};

export default function PartnerMessagingUsage() {
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [showFilters, setShowFilters] = useState(false);
  const [selectedView, setSelectedView] = useState<'overview' | 'detailed'>('overview');


  const { data: partnerData, isLoading: partnerLoading } = useApiQuery<{
    data: PartnerProfile;
  }>({
    queryKey: ["partner", "me"],
    request: { method: "GET", url: "/partner" } as ApiRequestOptions,
  });

  const partnerId = partnerData?.data?.id;

  const { data: apiData, isLoading, error, refetch } = useApiQuery<{ data: MessagingUsageResponse[] }>({
    queryKey: ['partner', 'messaging-usage', partnerId, format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
    request: {
      method: 'GET',
      url: `/partner/${partnerId}/messaging-usage/table?start=${format(startDate, 'yyyy-MM-dd')}&end=${format(endDate, 'yyyy-MM-dd')}`,
    },
    enabled: !!partnerId,
    retry: 1,
    // staleTime: 0,
    // refetchOnWindowFocus: false,
  });


  const usageData = useMemo(() => {
    if (!apiData) return { monthToDate: { smsCount: 0, emailCount: 0, smsCost: 0, emailCost: 0, totalCost: 0 }, dailyBreakdown: [] };

    return transformApiData(apiData);
  }, [apiData]);


  const filteredData = useMemo(() => {
    const filtered = usageData.dailyBreakdown.filter(day => {
      const dayDate = new Date(day.date);
      return isWithinInterval(dayDate, { start: startDate, end: endDate });
    });
    return filtered;
  }, [usageData.dailyBreakdown, startDate, endDate]);

  const filteredTotals = useMemo(() => {
    return filteredData.reduce(
      (acc, day) => ({
        smsCount: acc.smsCount + day.smsCount,
        emailCount: acc.emailCount + day.emailCount,
        smsCost: acc.smsCost + day.smsCost,
        emailCost: acc.emailCost + day.emailCost,
        totalCost: acc.totalCost + day.totalCost,
      }),
      { smsCount: 0, emailCount: 0, smsCost: 0, emailCost: 0, totalCost: 0 }
    );
  }, [filteredData]);


  if (isLoading) {
    return (
      <AdminLayout role="PARTNER">
        <div className="md:bg-gray-50 rounded-xl min-h-screen md::p-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">Loading messaging usage data...</span>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout role="PARTNER">
        <div className="md:bg-gray-50 rounded-xl min-h-screen md::p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-2">Failed to load messaging usage data</p>
              <p className="text-gray-500 text-sm">Please try again later</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout role="PARTNER">
      <div className=" rounded-xl min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Messaging Usage & Billing</h1>
              <p className="text-gray-600 text-sm">Monitor your organization's messaging usage and estimated costs</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => refetch()}
                disabled={isLoading}
                icon={<Refresh className="w-4 h-4" />}
                variant="primary"
                size="medium"
                label="Refresh"
              />
              <Button
                onClick={() => setShowFilters(!showFilters)}
                icon={<Filter className="w-4 h-4" />}
                label="Filters"
                size="medium"
                variant="tertiary"
              />
              {/* <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} /> */}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 z-[0] rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Important Disclaimer</h3>
              <p className="text-xs text-amber-700 mt-1">
                Messaging costs shown are estimates. Final charges are determined by your messaging provider.
              </p>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-6 overflow-hidden"
            >
              <ModernCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Date Range Filter</h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date: Date | null) => date && setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 z-[20000]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date: Date | null) => date && setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-5">
                    <Button
                      onClick={() => {
                        setStartDate(startOfMonth(new Date()));
                        setEndDate(endOfMonth(new Date()));
                      }}
                      variant="secondary"
                      size="medium"
                      label="This Month"

                    />
                  {/* <div className=""> */}
                    <Button
                      onClick={() => {
                        const lastMonth = new Date();
                        lastMonth.setMonth(lastMonth.getMonth() - 1);
                        setStartDate(startOfMonth(lastMonth));
                        setEndDate(endOfMonth(lastMonth));
                      }}
                      variant="secondary"
                      size="medium"
                      label="Last Month"
                    />

                  {/* </div> */}

                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={() => refetch()}
                      disabled={isLoading}
                      icon={<Refresh className="w-4 h-4" />}
                      variant="primary"
                      size="medium"
                      label="Apply Filters"
                    />
                  </div>
                </div>

              </ModernCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Toggle */}
        <div className="mb-6 flex gap-2">
          <Button
            size="large"
            variant="primary"
            onClick={() => setSelectedView('overview')}
            additionalClassname={`px-4 py-2 text-sm font-medium transition-colors ${selectedView === 'overview'
              ? 'text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}

            label="Overview"
          ></Button>

          <Button
            size="large"
            variant="primary"
            onClick={() => setSelectedView('detailed')}
            additionalClassname={`px-4 py-2 font-medium text-sm transition-colors ${selectedView === 'detailed'
              ? 'text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}

            label="Detailed Breakdown"
          ></Button>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={<MessageSquare className="w-6 h-6" />}
            title="SMS Messages"
            value={filteredTotals.smsCount.toLocaleString()}
            color="blue"
          />
          <MetricCard
            icon={<Mail className="w-6 h-6" />}
            title="Email Messages"
            value={filteredTotals.emailCount.toLocaleString()}
            color="green"
          />
          <MetricCard
            icon={<DollarSign className="w-6 h-6" />}
            title="Total Cost"
            value={`$${filteredTotals.totalCost.toFixed(2)}`}
            color="purple"
          />
          <MetricCard
            icon={<BarChart3 className="w-6 h-6" />}
            title="Avg Cost/Message"
            value={`$${(filteredTotals.totalCost / (filteredTotals.smsCount + filteredTotals.emailCount) || 0).toFixed(4)}`}
            color="orange"
          />
        </div>

        {/* Cost Breakdown */}
        <ModernCard className="mb-8">
          <div className="p-5">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Cost Breakdown</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="text-center p-5 bg-blue-50 rounded-xl">
                <MessageSquare className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">SMS Costs</h4>
                <p className="text-2xl font-bold text-blue-600">${filteredTotals.smsCost.toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-1">{filteredTotals.smsCount.toLocaleString()} messages</p>
                <p className="text-xs text-gray-500 mt-2">SMS costs calculated from usage data</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <Mail className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">Email Costs</h4>
                <p className="text-2xl font-bold text-green-600">${filteredTotals.emailCost.toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-1">{filteredTotals.emailCount.toLocaleString()} messages</p>
                <p className="text-xs text-gray-500 mt-2">Email costs calculated from usage data</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <DollarSign className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">Total Estimated Cost</h4>
                <p className="text-3xl font-bold text-purple-600">${filteredTotals.totalCost.toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-1">All channels combined</p>
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">SMS:</span>
                    <span className="font-medium">{((filteredTotals.smsCost / filteredTotals.totalCost) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{((filteredTotals.emailCost / filteredTotals.totalCost) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModernCard>

        {selectedView === 'detailed' && (
          <ModernCard>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-normal md:text-xl font-bold text-gray-900">Daily Breakdown</h3>
                <div className="text-sm text-gray-500">
                  {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Table Header */}
                  <div className="grid grid-cols-7 gap-2 md:gap-4 pb-4 border-b border-gray-200 text-xs md:text-sm font-semibold text-gray-700 min-w-[600px]">
                    <div className="text-xs md:text-sm">Date</div>
                    <div className="text-center text-xs md:text-sm">SMS Count</div>
                    <div className="text-center text-xs md:text-sm">Email Count</div>
                    <div className="text-center text-xs md:text-sm">SMS Cost</div>
                    <div className="text-center text-xs md:text-sm">Email Cost</div>
                    <div className="text-center text-xs md:text-sm">Daily Total</div>
                    <div className="text-center text-xs md:text-sm">Trend</div>
                  </div>

                  {/* Table Body */}
                  <div className="divide-y divide-gray-100 min-w-[600px]">
                    {filteredData.map((day, index) => (
                      <motion.div
                        key={day.date}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="grid grid-cols-7 gap-2 md:gap-4 py-2 md:py-3 text-xs md:text-sm hover:bg-gray-50 rounded-lg px-1 md:px-2 transition-colors"
                      >
                        <div className="font-medium text-gray-900 truncate">
                          {format(new Date(day.date), 'MMM dd, yyyy')}
                        </div>
                        <div className="text-center">
                          <span className="inline-flex items-center px-1 md:px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {day.smsCount}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="inline-flex items-center px-1 md:px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            {day.emailCount}
                          </span>
                        </div>
                        <div className="text-center font-mono text-blue-600 truncate">
                          ${day.smsCost.toFixed(4)}
                        </div>
                        <div className="text-center font-mono text-green-600 truncate">
                          ${day.emailCost.toFixed(4)}
                        </div>
                        <div className="text-center font-semibold text-purple-600 truncate">
                          ${day.totalCost.toFixed(4)}
                        </div>
                        <div className="text-center">
                          {day.trend === "UP" ? (
                            <TrendingUp className="w-3 h-3 md:w-4 md:h-4 text-red-500" />
                          ) : day.trend === "DOWN" ? (
                            <TrendingUp className="w-3 h-3 md:w-4 md:h-4 text-green-500 rotate-180" />
                          ) : (
                            <span className="text-gray-400 font-semibold text-xs md:text-sm">--</span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Table Footer */}
                  <div className="mt-4 pt-4 border-t border-gray-200 min-w-[600px]">
                    <div className="grid grid-cols-7 gap-2 md:gap-4 text-xs md:text-sm font-bold">
                      <div className="text-xs md:text-sm">Total</div>
                      <div className="text-center text-blue-600 text-xs md:text-sm">
                        {filteredTotals.smsCount.toLocaleString()}
                      </div>
                      <div className="text-center text-green-600 text-xs md:text-sm">
                        {filteredTotals.emailCount.toLocaleString()}
                      </div>
                      <div className="text-center font-mono text-blue-600 text-xs md:text-sm">
                        ${filteredTotals.smsCost.toFixed(2)}
                      </div>
                      <div className="text-center font-mono text-green-600 text-xs md:text-sm">
                        ${filteredTotals.emailCost.toFixed(2)}
                      </div>
                      <div className="text-center font-mono text-purple-600 text-sm md:text-lg text-xs md:text-sm">
                        ${filteredTotals.totalCost.toFixed(2)}
                      </div>
                      <div></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModernCard>
        )}
      </div>
    </AdminLayout>
  );
}
